<template>
    <v-data-table
        dense
        class="table application-table"
        :style="{ '--application-table-width': tableWidth + 'px' }"
        mobile-breakpoint="0"
        :items-per-page="50"
        :items="items"
        no-data-text="No applications found"
        :headers="columns"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :custom-sort="sortApplications"
        must-sort
        @update:sort-by="$emit('update:sort-by', $event)"
        @update:sort-desc="$emit('update:sort-desc', $event)"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100, -1] }"
    >
        <template #item.application="{ item: { id, name, ns, color } }">
            <div class="application">
                <div class="status" :class="color" />
                <div class="name" :title="ns ? `${name} (ns:${ns})` : name">
                    <router-link :to="link(id, undefined)">{{ name }}</router-link>
                    <span v-if="ns" class="caption grey--text"> (ns:{{ ns }})</span>
                </div>
            </div>
        </template>
        <template #item.cluster="{ item: { cluster } }">
            <span class="cluster grey--text"> {{ cluster }} </span>
        </template>
        <template #item.type="{ item: { id, type } }">
            <div v-if="type" class="d-flex align-center">
                <AppIcon :icon="type.icon" class="mr-1" />

                <router-link v-if="type.report" :to="link(id, type.report)" class="type" :class="type.status">
                    {{ type.name }}
                </router-link>
                <div v-else class="type">
                    {{ type.name }}
                </div>
            </div>
        </template>
        <template #item.errors="{ item: { id, errors: param } }">
            <router-link :to="link(id, 'SLO')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.latency="{ item: { id, latency: param } }">
            <router-link :to="link(id, 'SLO')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.upstreams="{ item: { id, upstreams: param } }">
            <router-link :to="link(id, 'SLO')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.instances="{ item: { id, instances: param } }">
            <router-link :to="link(id, 'Instances')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.restarts="{ item: { id, restarts: param } }">
            <router-link :to="link(id, 'Instances')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.cpu="{ item: { id, cpu: param } }">
            <router-link :to="link(id, 'CPU')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.memory="{ item: { id, memory: param } }">
            <router-link :to="link(id, 'Memory')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.disk_io_load="{ item: { id, disk_io_load: param } }">
            <router-link :to="link(id, 'Storage')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.disk_usage="{ item: { id, disk_usage: param } }">
            <router-link :to="link(id, 'Storage')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.network="{ item: { id, network: param } }">
            <router-link :to="link(id, 'Net')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.dns="{ item: { id, dns: param } }">
            <router-link :to="link(id, 'DNS')" class="value" :class="param.status">{{ param.value || '–' }}</router-link>
        </template>
        <template #item.logs="{ item: { id, logs: param } }">
            <router-link :to="link(id, 'Logs', { query: JSON.stringify({ source: 'agent', view: 'patterns' }) })" class="logs">
                <div class="value">{{ param.value || '–' }}</div>
                <v-sparkline
                    v-if="param.chart"
                    :value="param.chart.map((v) => (v === null ? 0 : v))"
                    fill
                    smooth
                    padding="4"
                    :color="`blue ${$vuetify.theme.dark ? '' : 'lighten-4'}`"
                    height="30"
                    width="120"
                />
            </router-link>
        </template>
    </v-data-table>
</template>

<script>
import AppIcon from '@/components/AppIcon.vue';
import { sortApplications } from '@/utils/application-sorting';

// Every service table uses the same column geometry, independent of its rows.
const columnWidths = {
    application: 280,
    cluster: 180,
    type: 128,
    errors: 80,
    latency: 90,
    upstreams: 104,
    instances: 96,
    restarts: 96,
    cpu: 72,
    memory: 80,
    disk_io_load: 96,
    disk_usage: 80,
    network: 96,
    dns: 80,
    logs: 136,
};

export default {
    components: { AppIcon },

    props: {
        headers: Array,
        items: Array,
        sortBy: String,
        sortDesc: Boolean,
    },

    computed: {
        columns() {
            return (this.headers || []).map((header) => ({ ...header, width: columnWidths[header.value] || 96 }));
        },
        tableWidth() {
            return this.columns.reduce((width, column) => width + column.width, 0);
        },
    },

    methods: {
        sortApplications,
        link(id, report, query) {
            return { name: 'overview', params: { view: 'applications', id, report }, query: { ...query, ...this.$utils.contextQuery() } };
        },
    },
};
</script>

<style scoped>
.table.application-table:deep(table) {
    table-layout: fixed;
    width: 100%;
    min-width: var(--application-table-width);
}
.table:deep(tr:hover) {
    background-color: unset !important;
}
.table:deep(th) {
    white-space: nowrap;
}
.table:deep(th),
.table:deep(td) {
    padding: 4px 8px !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.table:deep(td:has(.application)) {
    padding-left: 0 !important;
}
.table .application {
    display: flex;
    gap: 4px;
    min-width: 0;
}
.table .application .status {
    height: 20px;
    width: 4px;
    flex-shrink: 0;
}
.table .application .name {
    min-width: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .cluster {
    display: block;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table .logs {
    display: block;
    position: relative;
    height: 100%;
    color: inherit;
}
.table .logs .value {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table:deep(td:has(.logs)) {
    padding: 0 !important;
}

.type {
    opacity: 60%;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}
.type.unknown {
    opacity: 100%;
    border-bottom: 2px solid #ab47bc !important;
    background-color: unset !important;
}
.type.ok {
    opacity: 100%;
}
.type.critical,
.type.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}

.value {
    color: inherit;
    opacity: 60%;
    white-space: nowrap;
}
.value.critical,
.value.warning {
    opacity: 100%;
    border-bottom: 2px solid red !important;
    background-color: unset !important;
}
</style>
